// wiring.ts — 데이터·API 배선(개발자 소유). 컨덕터는 이 파일이 없을 때 한 번 만들고 다시 건드리지 않는다.
//  화면 파일(page.generated.tsx)은 컨덕터가 재생성 때 통째로 덮어쓴다 — 배선은 여기에만 둔다.
//  GI17 출고취소(비제스트) 1.0 → 2.0 (LOGISPRD-16035). 조회 전용. moms-be `/api/v1/bizest-cancel-results/search` 만 부른다.
//  1.0 의 저장·송신·엑셀 업로드는 운영에서 바꿀 행이 남지 않아 옮기지 않았다(moms-be LLD-outbound-gi17-bizest-cancel-screen 1장).
import { useMemo, useSyncExternalStore } from 'react'
import dayjs from 'dayjs'
import type { Wiring } from './page.generated'
import { invalidError, failureError, notice } from '@/components/pbo/notice'
import { useGetExportCombo } from '@/services/excel/useExcelQuries'
import { callToAddExportLog } from '@/services/excel/apis'
import { bizestCancelResultQueryKeys, useGetBizestCancelResults } from '@/services/outbound/bizestCancelResult/useBizestCancelResultQueries'
import type { BizestCancelResultRowDto, BizestCancelResultSearchReq } from '@/services/outbound/bizestCancelResult/types'
import {
  BIZEST_CANCEL_DEFAULT_TODAY_SHIPPER,
  BIZEST_CANCEL_REASON_CODES,
  BIZEST_CANCEL_RESULT_MENU_CODE,
  BIZEST_CANCEL_SHIPPERS,
  DEFAULT_PERIOD_DAYS_OTHER_SHIPPER,
  MAX_PERIOD_DAYS,
  MAX_SEARCH_VALUES,
} from '@/services/outbound/bizestCancelResult/consts'
import { shipperScopeOf } from '@/components/system/shipperScope'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { queryClient } from '@/utils/queryClient'
import { ExcelActions } from '@/utils/Excel'

type Row = Record<string, unknown>

/** page.schema.json bcDownloadModal 상세사유 minLength 와 같은 값이어야 한다. 스키마를 바꾸면 여기도 바꾼다. */
const MIN_DETAIL_LENGTH = 5
const YN = (flag: boolean): 'Y' | 'N' => (flag ? 'Y' : 'N')
const text = (v: unknown): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '')
const list = (v: unknown): string[] => (Array.isArray(v) ? v : [v]).map(text).map((s) => s.trim()).filter(Boolean)
const yyyymmdd = (v: string) => v.replaceAll('-', '')

/** 사유코드 표시 — 1.0 GI17.js valueFormatter 와 같이 '코드 이름'. 목록에 없는 값은 원문 그대로. */
const REASON_LABEL = new Map<string, string>(BIZEST_CANCEL_REASON_CODES.map((o) => [o.value, o.label]))
export const reasonLabelOf = (code: string | null): string => (code ? (REASON_LABEL.get(code) ?? code) : '')

/** 행 키 — (화주, 주문번호, 주문일련번호)가 BE 의 행 식별자다. */
export const rowKeyOf = (shipperCode: string, orderNumber: string, orderOptionNumber: string) => `${shipperCode}|${orderNumber}|${orderOptionNumber}`

export const toViewRow = (dto: BizestCancelResultRowDto): Row => ({
  rowKey: rowKeyOf(dto.shipperCode, dto.orderNumber, dto.orderOptionNumber),
  shipperCode: dto.shipperCode,
  clsDate: dto.clsDate ?? '',
  orderNumber: dto.orderNumber,
  orderOptionNumber: dto.orderOptionNumber,
  internalProcessed: YN(dto.internalProcessed),
  transmitResult: YN(dto.transmitResult),
  reasonCode: reasonLabelOf(dto.reasonCode),
  remark: dto.remark ?? '',
  insertedAt: dto.insertedAt ?? '',
})

/** 조회영역 값 → 조회 요청. 화주·기간이 비면 null(조회하지 않음). */
export const toSearchReq = (filters: Record<string, unknown>): BizestCancelResultSearchReq | null => {
  const shipperCode = text(filters.SPR_NM)
  const range = Array.isArray(filters.CLS_DATE) ? (filters.CLS_DATE as string[]) : []
  if (!shipperCode || !range[0] || !range[1]) return null
  const orderNumbers = list(filters.ORD_NO)
  const orderOptionNumbers = list(filters.ORD_OPT_NO)
  const transmitResult = text(filters.OIF_API_FLAG)
  const reasonCode = text(filters.RSN_CODE)
  return {
    shipperCode,
    clsDateFrom: yyyymmdd(range[0]),
    clsDateTo: yyyymmdd(range[1]),
    ...(orderNumbers.length ? { orderNumbers } : {}),
    ...(orderOptionNumbers.length ? { orderOptionNumbers } : {}),
    ...(transmitResult === 'Y' || transmitResult === 'N' ? { transmitResult } : {}),
    ...(reasonCode ? { reasonCode } : {}),
  }
}

// ── 화주 범위 ────────────────────────────────────────────────────────────────
// 1.0 메타는 MUSINSA·NIKE·NIKESAMPLE 만 있다. 화주 계정은 자기 화주로 잠근다(2.0 공용 규칙 shipperScopeOf).
const scope = () => shipperScopeOf(AuthStorage.user.get())
const shipperOptions = () => {
  const s = scope()
  if (s.locked) return [{ value: s.shipperCode, label: s.shipperName || s.shipperCode }]
  return BIZEST_CANCEL_SHIPPERS.map((code) => ({ value: code, label: code }))
}
const SHIPPER_SET = new Set<string>(BIZEST_CANCEL_SHIPPERS)
const defaultShipper = (): string => {
  const s = scope()
  if (s.locked) return s.shipperCode
  return SHIPPER_SET.has(s.shipperCode) ? s.shipperCode : BIZEST_CANCEL_SHIPPERS[0]
}
/** 1.0 systemTask.py:136 — MUSINSA 는 당일, 그 밖의 화주는 7일 전부터 오늘까지 */
export const defaultClsDate = (shipperCode: string, today = dayjs()): [string, string] => [
  (shipperCode === BIZEST_CANCEL_DEFAULT_TODAY_SHIPPER ? today : today.subtract(DEFAULT_PERIOD_DAYS_OTHER_SHIPPER, 'day')).format('YYYY-MM-DD'),
  today.format('YYYY-MM-DD'),
]

// ── 모듈 상태: [조회] 로 확정한 조건 ─────────────────────────────────────────────
// 표 슬롯은 입력 중인 조회영역 값을 받으므로, [조회] 를 누른 조건을 따로 들고 있어야 입력만으로 재조회되지 않는다.
let params: BizestCancelResultSearchReq | null = null
const listeners = new Set<() => void>()
const setParams = (next: BizestCancelResultSearchReq | null) => {
  params = next
  listeners.forEach((l) => l())
}
const subscribe = (l: () => void) => {
  listeners.add(l)
  return () => listeners.delete(l)
}
/** 마운트 때 비운다 — 탭을 닫았다 다시 열면 이전 조건이 남지 않게(배선 체크 ⑤). */
export const resetBizestCancelStore = () => setParams(null)

const refetch = () => queryClient.invalidateQueries({ queryKey: bizestCancelResultQueryKeys.all })

/** 엑셀 컬럼 — 표 exportColumns 와 같은 순서. */
export const EXPORT_COLUMNS = [
  { field: 'clsDate', headerName: '취소요청일자' },
  { field: 'orderNumber', headerName: '주문번호' },
  { field: 'orderOptionNumber', headerName: '주문일련번호' },
  { field: 'reasonCode', headerName: '사유코드' },
  { field: 'remark', headerName: '비고' },
]

export const wiring: Wiring = {
  rows: {
    ROWS: () => {
      const p = useSyncExternalStore(subscribe, () => params)
      const { data, isFetching } = useGetBizestCancelResults(p)
      const rows = useMemo(() => (data ?? []).map(toViewRow), [data])
      return { rows, total: rows.length, loading: isFetching }
    },
  },
  actions: {
    search: (filters) => {
      if (!text(filters.SPR_NM)) {
        notice.invalid('화주명을 선택해 주세요.')
        return false
      }
      const next = toSearchReq(filters)
      if (!next) {
        notice.invalid('취소요청일자를 입력해 주세요.')
        return false
      }
      if (dayjs(next.clsDateTo, 'YYYYMMDD').diff(dayjs(next.clsDateFrom, 'YYYYMMDD'), 'day') >= MAX_PERIOD_DAYS) {
        notice.invalid(`취소요청일자는 최대 ${MAX_PERIOD_DAYS}일까지 조회할 수 있습니다.`)
        return false
      }
      if ((next.orderNumbers?.length ?? 0) > MAX_SEARCH_VALUES || (next.orderOptionNumbers?.length ?? 0) > MAX_SEARCH_VALUES) {
        notice.invalid(`주문번호·주문일련번호는 각각 최대 ${MAX_SEARCH_VALUES}건까지 조회할 수 있습니다.`)
        return false
      }
      // toSearchReq 가 늘 같은 순서로 키를 만들어서 문자열 비교로 같은 조건인지 가린다.
      const same = JSON.stringify(next) === JSON.stringify(params)
      setParams(next)
      // 같은 조건 재조회는 queryKey 가 같아 요청이 나가지 않는다 — 무효화로 다시 받는다(배선 체크 ⑥).
      if (same) refetch().catch(() => undefined)
      return undefined
    },
    bcDownloadModal_ok: async (ctx) => {
      const reason = text(ctx.form?.reason)
      const detail = text(ctx.form?.detail).trim()
      if (!reason) throw invalidError('사유코드를 선택해 주세요.')
      if (!detail) throw invalidError('상세사유를 입력해 주세요.')
      if (detail.length < MIN_DETAIL_LENGTH) throw invalidError(`상세사유는 ${MIN_DETAIL_LENGTH}글자 이상 입력해 주세요.`)
      const rows = ctx.visible ?? []
      if (!rows.length) throw invalidError('다운로드할 데이터가 없습니다. 조회 후 다운로드해 주세요.')
      // 감사로그를 먼저 남긴다 — 기록이 실패하면 파일을 내려주지 않는다(사유 없는 반출 방지, CR-01).
      await callToAddExportLog({
        menuCode: BIZEST_CANCEL_RESULT_MENU_CODE,
        apiName: `${BIZEST_CANCEL_RESULT_MENU_CODE}_LIST_EXPORT`,
        reason: `${reason}_${detail}`,
      }).catch(() => {
        throw failureError('다운로드 기록을 남기지 못해 다운로드하지 않았습니다. 잠시 뒤 다시 시도해 주세요.')
      })
      await new ExcelActions().download({ columnDefs: EXPORT_COLUMNS, rowData: rows, fileName: '출고취소(비제스트)' })
      return '다운로드했습니다.'
    },
  },
  form: {
    bcDownloadModal: (state) => state,
  },
  options: {
    BCSHIPPER_OPTIONS: () => shipperOptions(),
    BCRSNCODE_OPTIONS: () => BIZEST_CANCEL_REASON_CODES.map((o) => ({ ...o })),
    // 다운로드 사유코드 — 콤보 DOWNLOAD_CD(2.0 공통)
    // 배선 계약: options 슬롯은 생성본 컴포넌트 본문에서 매 렌더 같은 순서로 불린다 — 안에서 조회 훅을 써도 된다(wiring.skeleton.ts).
    REASONRADIO_OPTIONS: (demo) => {
      const { data } = useGetExportCombo({ errorToast: true })
      const cds = data?.DOWNLOAD_CD?.COMBO_OPT_CD ?? []
      const nms = data?.DOWNLOAD_CD?.COMBO_OPT_NM ?? []
      const options = cds.map((value, i) => ({ value, label: nms[i] ?? value })).filter((o) => o.value)
      return options.length ? options : demo
    },
  },
  filterDisabled: {
    bcFilterCard: () => ({ SPR_NM: scope().locked }),
  },
  filterDefaults: {
    bcFilterCard: () => {
      const shipperCode = defaultShipper()
      return { SPR_NM: shipperCode, CLS_DATE: defaultClsDate(shipperCode) }
    },
  },
}
